import httpService from './httpService';
import {
  StorageUsage,
  StorageDirectory,
  DirectoryWithChildren,
  StorageItem,
  CreateDirectoryRequest,
  UpdateDirectoryRequest,
  MoveDirectoryRequest,
  UpdateItemRequest,
  MoveItemRequest,
  MoveBatchItemsRequest,
  DeleteBatchItemsRequest,
  StorageLimitError,
} from '../types/storage';

const storageService = {
  /**
   * Fetches the storage usage for the current business.
   */
  getStorageUsage: async (): Promise<StorageUsage> => {
    return httpService.get('/business/storage/usage').then(({ data }): StorageUsage => {
      return {
        total: data.total,
        photoAlbums: data.photoAlbums,
        fileStorage: data.fileStorage,
        maxUsage: data.maxUsage,
        updatedAt: new Date(data.updatedAt),
      };
    });
  },

  // Directory Operations

  /**
   * Get the complete directory tree with nested children and items.
   */
  getDirectoryTree: async (): Promise<DirectoryWithChildren[]> => {
    return httpService.get('/business/storage/directory/tree').then(({ data }) => data);
  },

  /**
   * Get a specific directory with immediate children and items.
   */
  getDirectory: async (directoryId: number): Promise<DirectoryWithChildren> => {
    return httpService.get(`/business/storage/directory/${directoryId}`).then(({ data }) => data);
  },

  /**
   * Create a new directory.
   */
  createDirectory: async (request: CreateDirectoryRequest): Promise<StorageDirectory> => {
    return httpService.post('/business/storage/directory', request).then(({ data }) => data);
  },

  /**
   * Update directory name (rename).
   */
  updateDirectory: async (
    directoryId: number,
    request: UpdateDirectoryRequest
  ): Promise<StorageDirectory> => {
    return httpService.put(`/business/storage/directory/${directoryId}`, request).then(({ data }) => data);
  },

  /**
   * Move directory to a new parent.
   */
  moveDirectory: async (
    directoryId: number,
    request: MoveDirectoryRequest
  ): Promise<StorageDirectory> => {
    return httpService
      .put(`/business/storage/directory/${directoryId}/move`, request)
      .then(({ data }) => data);
  },

  /**
   * Delete a directory (cascades to subdirectories and files).
   */
  deleteDirectory: async (directoryId: number): Promise<void> => {
    return httpService.delete(`/business/storage/directory/${directoryId}`);
  },

  // Item (File) Operations

  /**
   * Get items, optionally filtered by directory.
   * @param directoryId - undefined for all items, null for root items, number for specific directory
   */
  getItems: async (directoryId?: number | null): Promise<StorageItem[]> => {
    const params: Record<string, string> = {};
    if (directoryId !== undefined) {
      params.directoryId = directoryId === null ? 'null' : String(directoryId);
    }
    return httpService
      .get('/business/storage/item', { params })
      .then(({ data }) => data);
  },

  /**
   * Upload a single file.
   * Returns the created item or a storage limit error (status 206).
   */
  uploadFile: async (
    file: File,
    directoryId?: number
  ): Promise<StorageItem | StorageLimitError> => {
    const formData = new FormData();
    formData.append('file', file);
    if (directoryId !== undefined) {
      formData.append('directoryId', String(directoryId));
    }

    return httpService
      .post('/business/storage/item', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(({ data, status }) => {
        if (status === 206) {
          return data as StorageLimitError;
        }
        return data as StorageItem;
      });
  },

  /**
   * Upload multiple files.
   * Returns the created items or a storage limit error (status 206).
   */
  uploadFiles: async (
    files: File[],
    directoryId?: number,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<StorageItem[] | StorageLimitError> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    if (directoryId !== undefined) {
      formData.append('directoryId', String(directoryId));
    }

    return httpService
      .post('/business/storage/item/multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      })
      .then(({ data, status }) => {
        if (status === 206) {
          return data as StorageLimitError;
        }
        return data as StorageItem[];
      });
  },

  /**
   * Update item filename (rename).
   */
  updateItem: async (itemId: number, request: UpdateItemRequest): Promise<StorageItem> => {
    return httpService.put(`/business/storage/item/${itemId}`, request).then(({ data }) => data);
  },

  /**
   * Move item to a new directory.
   */
  moveItem: async (itemId: number, request: MoveItemRequest): Promise<StorageItem> => {
    return httpService.put(`/business/storage/item/${itemId}/move`, request).then(({ data }) => data);
  },

  /**
   * Move multiple items to a new directory.
   */
  moveBatchItems: async (request: MoveBatchItemsRequest): Promise<{ count: number }> => {
    return httpService.put('/business/storage/item/move', request).then(({ data }) => data);
  },

  /**
   * Delete a single item.
   */
  deleteItem: async (itemId: number): Promise<void> => {
    return httpService.delete(`/business/storage/item/${itemId}`);
  },

  /**
   * Delete multiple items.
   */
  deleteBatchItems: async (request: DeleteBatchItemsRequest): Promise<{ count: number }> => {
    return httpService.delete('/business/storage/item', { data: request }).then(({ data }) => data);
  },
};

export default storageService;
